import {
	ScheduleFrequencies,
	DefaultDeliveryTime,
	DefaultTimezone,
} from "constants/reportSubscriptions";
import { formatFilterSummary } from "helpers/reportSubscriptions";

// Canonical frontend report-type strings (camelCase) <-> backend ReportType
// enum names (PascalCase, what the API serialises). One entry per backend
// value so the reverse map is unambiguous.
//
// Keep in sync with EMI3.Core.Reporting.ReportType when new entries land.
const REPORT_TYPE_TO_API = {
	defectsSummary: "DefectsSummary",
	defectsByRisk: "DefectsByRisk",
	defectsByStatus: "DefectsByStatus",
	defectsExcel: "DefectsCsv",
	servicesList: "ServicesList",
	servicesListCsv: "ServicesListCsv",
	cmDataExport: "CmDataExport",
	serviceSheet: "ServiceSheet",
	completedService: "CompletedService",
	partsByWorkOrder: "PartsByWorkOrder",
	partsByTask: "PartsByTask",
	partsSummary: "PartsSummary",
	toolsByTask: "ToolsByTask",
	toolsSummary: "ToolsSummary",
	tasksWhereAssetUsed: "TasksWhereAssetUsed",
	modelTask: "ModelTask",
	modelTaskNotes: "ModelTaskNotes",
	auditHistory: "AuditHistory",
	auditHistoryCsv: "AuditHistoryCsv",
};

// Legacy upper-snake aliases used by older constants/ReportTypes entries and
// hardcoded reportType strings on the Defects/Services pages. Resolved to the
// canonical camelCase key before the forward lookup so the reverse map stays
// unambiguous (see fromApiReportType).
const REPORT_TYPE_ALIASES = {
	SERVICE_REPORT: "servicesList",
	SERVICE_EXCEL: "servicesListCsv",
	CM_DATA_EXPORT: "cmDataExport",
	PARTSBYWORKORDER: "partsByWorkOrder",
};

const API_TO_REPORT_TYPE = Object.fromEntries(
	Object.entries(REPORT_TYPE_TO_API).map(([camel, pascal]) => [pascal, camel])
);

// Mirrors EMI3.Core.Reporting.ReportType. The backend serialises this enum as
// the integer value when no JsonStringEnumConverter is in scope, so fromApi
// has to handle both shapes.
const REPORT_TYPE_BY_INDEX = {
	1: "defectsSummary",
	2: "defectsByRisk",
	3: "defectsByStatus",
	4: "defectsExcel",
	10: "servicesList",
	11: "serviceSheet",
	12: "completedService",
	13: "servicesListCsv",
	14: "cmDataExport",
	20: "partsByWorkOrder",
	21: "partsByTask",
	22: "partsSummary",
	23: "toolsByTask",
	24: "toolsSummary",
	25: "tasksWhereAssetUsed",
	26: "modelTask",
	27: "modelTaskNotes",
	30: "auditHistory",
	31: "auditHistoryCsv",
};

// .NET's DayOfWeek enum serialises as integers (Sunday=0..Saturday=6) when
// no JsonStringEnumConverter is in scope. We accept both forms on the way in
// and send the numeric form on the way out so it round-trips cleanly.
const API_TO_DAY = {
	0: "Sunday",
	1: "Monday",
	2: "Tuesday",
	3: "Wednesday",
	4: "Thursday",
	5: "Friday",
	6: "Saturday",
	Sunday: "Sunday",
	Monday: "Monday",
	Tuesday: "Tuesday",
	Wednesday: "Wednesday",
	Thursday: "Thursday",
	Friday: "Friday",
	Saturday: "Saturday",
};

const DAY_TO_API = {
	Sunday: 0,
	Monday: 1,
	Tuesday: 2,
	Wednesday: 3,
	Thursday: 4,
	Friday: 5,
	Saturday: 6,
};

const fromApiDays = (days) =>
	Array.isArray(days) ? days.map((d) => API_TO_DAY[d] ?? d) : [];

const toApiDays = (days) =>
	Array.isArray(days) ? days.map((d) => DAY_TO_API[d] ?? d) : [];

export const toApiReportType = (frontendType) => {
	const canonical = REPORT_TYPE_ALIASES[frontendType] ?? frontendType;
	return REPORT_TYPE_TO_API[canonical] ?? frontendType;
};

export const fromApiReportType = (apiType) => {
	if (typeof apiType === "number") return REPORT_TYPE_BY_INDEX[apiType] ?? apiType;
	return API_TO_REPORT_TYPE[apiType] ?? apiType;
};


// "07:00" or "07:00:00" -> "07:00"
const trimSeconds = (timeOfDay) => {
	if (!timeOfDay) return DefaultDeliveryTime;
	const [hh, mm] = timeOfDay.split(":");
	return `${hh}:${mm}`;
};

// API DTO -> UI view-model used by the profile card and the modal edit flow.
export const dtoToViewModel = (dto) => {
	const filterContext = dto.filterContext || {};
	// Prefer the saved human-readable snapshot captured at subscribe time.
	// Falls back to deriving from filterContext for legacy rows that pre-date
	// the FilterSummary column.
	const filters = Array.isArray(dto.filterSummary) && dto.filterSummary.length
		? dto.filterSummary
		: formatFilterSummary(filterContext);

	return {
		id: dto.id,
		name: dto.name || "",
		reportType: fromApiReportType(dto.reportType),
		reportName: dto.reportTypeName,
		filters,
		filterContext,
		// All seven days set => "Daily"; subset => "Weekly".
		frequency:
			(dto.daysOfWeek?.length ?? 0) === 7
				? ScheduleFrequencies.Daily
				: ScheduleFrequencies.Weekly,
		daysOfWeek: fromApiDays(dto.daysOfWeek),
		timeOfDay: trimSeconds(dto.timeOfDay),
		timezone: dto.timezone || DefaultTimezone,
		deliveryEmail: dto.deliveryEmail || null,
		isActive: dto.isActive,
		lastRunAt: dto.lastRunAt,
		lastRunStatus: dto.lastRunStatus,
		lastRunError: dto.lastRunError,
	};
};

// UI form values -> API request body. filterContext arrives already in the
// flat backend report-DTO shape (callers use useReportFilterContext() from
// FilterContext, which spreads each filter's reportParams). Passed through
// verbatim. filterSummary is the [{label, value}] snapshot captured on the
// source page so the profile card can render it without a live FilterContext.
export const formValuesToApiRequest = (values, filterContext, filterSummary) => ({
	name: values.name?.trim() || null,
	reportType: toApiReportType(values.reportType),
	filterContext: filterContext ?? {},
	filterSummary: Array.isArray(filterSummary) && filterSummary.length ? filterSummary : null,
	daysOfWeek: toApiDays(values.daysOfWeek),
	timeOfDay: values.timeOfDay,
	timezone: values.timezone || DefaultTimezone,
	deliveryEmail: values.deliveryEmail || null,
});
